/**
 * Display Template Input
 * An input that allows entering a display template with field placeholders
 * Fields can be selected from a dropdown including nested relational fields
 * 
 * For M2M junction tables, this shows:
 * - Junction table fields (id, post_id, Nutrition_Recommendations_id)
 * - Expandable related fields (post_id.name, Nutrition_Recommendations_id.name)
 */

import { useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Plus, ChevronRight, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFields, type Field } from '@/hooks/useFields';
import { useRelations } from '@/hooks/useRelations';
import { type ApiListResponse } from '@/lib/api';

interface DisplayTemplateInputProps {
    value: string;
    onChange: (value: string) => void;
    collection: string; // The collection to get fields from (junction collection for M2M)
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

interface FieldNode {
    field: string;        // The field key (e.g., "post_id" or "post_id.name")
    name: string;         // Display name
    type: string;         // Field type
    relatedCollection?: string;
    isExpandable?: boolean;
    depth: number;
}

export function DisplayTemplateInput({
    value,
    onChange,
    collection,
    placeholder = 'e.g., {{name}} - {{id}}',
    disabled = false,
    className,
}: DisplayTemplateInputProps) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
    const [loadingRelated, setLoadingRelated] = useState<Set<string>>(new Set());
    const [relatedFieldsCache, setRelatedFieldsCache] = useState<Record<string, Field[]>>({});

    // Fetch fields for the main collection
    const { fields: mainFields, loading: mainFieldsLoading } = useFields({ collection });

    // Fetch all relations
    const { data: relations } = useRelations();

    // Get related collection for a field
    const getRelatedCollection = useCallback((fieldName: string): string | undefined => {
        if (!relations) return undefined;

        // Find relation where this collection is the many side and fieldName is the field
        const relation = relations.find(
            r => r.collection === collection && r.field === fieldName && r.related_collection
        );

        return relation?.related_collection || undefined;
    }, [relations, collection]);

    // Build field tree for the dropdown
    const fieldNodes = useMemo<FieldNode[]>(() => {
        if (!mainFields) return [];

        const nodes: FieldNode[] = [];

        // Add main collection fields
        for (const field of mainFields) {
            if (field.meta?.hidden) continue;

            const relatedCollection = getRelatedCollection(field.field);
            const isM2O = field.meta?.special?.includes('m2o') || !!relatedCollection;

            nodes.push({
                field: field.field,
                name: field.meta?.note || field.field.replace(/_/g, ' '),
                type: field.type || 'unknown',
                relatedCollection,
                isExpandable: isM2O && !!relatedCollection,
                depth: 0,
            });

            // If expanded, add related fields
            if (expandedFields.has(field.field) && relatedCollection) {
                const relatedFields = relatedFieldsCache[relatedCollection];
                if (relatedFields) {
                    for (const relatedField of relatedFields) {
                        if (relatedField.meta?.hidden) continue;
                        nodes.push({
                            field: `${field.field}.${relatedField.field}`,
                            name: relatedField.meta?.note || relatedField.field.replace(/_/g, ' '),
                            type: relatedField.type || 'unknown',
                            depth: 1,
                        });
                    }
                }
            }
        }

        return nodes;
    }, [mainFields, expandedFields, relatedFieldsCache, getRelatedCollection]);

    // Fetch related collection fields when expanded
    const fetchRelatedFields = useCallback(async (fieldName: string, relatedCollection: string) => {
        if (relatedFieldsCache[relatedCollection]) return;

        setLoadingRelated(prev => new Set([...prev, fieldName]));

        try {
            // Use the API to fetch fields for the related collection
            const { api } = await import('@/lib/api');
            const response = await api.get<ApiListResponse<Field>>(`/fields/${relatedCollection}`);
            setRelatedFieldsCache(prev => ({
                ...prev,
                [relatedCollection]: response.data || [],
            }));
        } catch (error) {
            console.error('Failed to fetch related fields:', error);
        } finally {
            setLoadingRelated(prev => {
                const newSet = new Set(prev);
                newSet.delete(fieldName);
                return newSet;
            });
        }
    }, [relatedFieldsCache]);

    // Toggle field expansion
    const toggleExpand = useCallback((fieldName: string, relatedCollection?: string) => {
        setExpandedFields(prev => {
            const newSet = new Set(prev);
            if (newSet.has(fieldName)) {
                newSet.delete(fieldName);
            } else {
                newSet.add(fieldName);
                // Fetch related fields if needed
                if (relatedCollection && !relatedFieldsCache[relatedCollection]) {
                    fetchRelatedFields(fieldName, relatedCollection);
                }
            }
            return newSet;
        });
    }, [relatedFieldsCache, fetchRelatedFields]);

    // Parse the value to extract field placeholders
    const parsedValue = useMemo(() => {
        const regex = /({{.*?}})/g;
        const parts = value.split(regex).filter(Boolean);

        return parts.map((part, index) => {
            if (part.startsWith('{{') && part.endsWith('}}')) {
                const fieldKey = part.slice(2, -2).trim();
                return {
                    type: 'field' as const,
                    value: fieldKey,
                    display: fieldKey.replace(/_/g, ' '),
                    index,
                };
            }
            return {
                type: 'text' as const,
                value: part,
                display: part,
                index,
            };
        });
    }, [value]);

    // Add field to the template
    const addField = useCallback((fieldKey: string) => {
        const newValue = value + `{{${fieldKey}}}`;
        onChange(newValue);
        setPopoverOpen(false);
    }, [value, onChange]);

    // Remove a field placeholder
    const removeField = useCallback((fieldKey: string) => {
        const regex = new RegExp(`\\{\\{${fieldKey.replace('.', '\\.')}\\}\\}`, 'g');
        const newValue = value.replace(regex, '');
        onChange(newValue);
    }, [value, onChange]);

    // Handle direct input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e.target.value);
    };

    // Render a field item in the dropdown
    const renderFieldItem = (node: FieldNode) => {
        const isExpanded = expandedFields.has(node.field);
        const isLoading = loadingRelated.has(node.field);

        return (
            <div key={node.field}>
                <div
                    className={cn(
                        "flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent/50 cursor-pointer",
                        node.depth > 0 && "pl-6 border-l-2 border-muted ml-2"
                    )}
                >
                    {node.isExpandable && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(node.field, node.relatedCollection);
                            }}
                            className="shrink-0 p-0.5 hover:bg-accent rounded"
                        >
                            {isLoading ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <ChevronRight className={cn(
                                    "h-3 w-3 transition-transform",
                                    isExpanded && "rotate-90"
                                )} />
                            )}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={() => addField(node.field)}
                        className="flex-1 text-left flex items-center gap-2"
                    >
                        <span className="capitalize truncate">{node.name}</span>
                        <span className="text-xs text-muted-foreground font-mono shrink-0">
                            {node.type}
                        </span>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className={cn("relative space-y-2", className)}>
            {/* Visual representation with badges */}
            <div
                className={cn(
                    "flex flex-wrap items-center gap-1 min-h-10 px-3 py-2 rounded-md border border-input bg-background",
                    "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                    disabled && "opacity-50 cursor-not-allowed"
                )}
            >
                {parsedValue.length > 0 ? (
                    parsedValue.map((part, idx) => (
                        part.type === 'field' ? (
                            <Badge
                                key={idx}
                                variant="secondary"
                                className="gap-1 cursor-pointer hover:bg-secondary/80 font-mono text-xs"
                            >
                                {part.display}
                                {!disabled && (
                                    <X
                                        className="h-3 w-3 cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeField(part.value);
                                        }}
                                    />
                                )}
                            </Badge>
                        ) : (
                            <span key={idx} className="text-sm">{part.display}</span>
                        )
                    ))
                ) : (
                    <span className="text-muted-foreground text-sm">{placeholder}</span>
                )}

                {/* Add field button */}
                {!disabled && (
                    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 ml-auto shrink-0"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent
                            align="end"
                            className="w-72 p-0"
                            onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                            <div className="p-2 border-b">
                                <p className="text-xs font-medium text-muted-foreground">
                                    Add Field
                                </p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                    Click arrow to expand related fields
                                </p>
                            </div>
                            <ScrollArea className="h-64">
                                {mainFieldsLoading ? (
                                    <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Loading fields...
                                    </div>
                                ) : fieldNodes.length === 0 ? (
                                    <div className="p-4 text-sm text-muted-foreground">
                                        No fields available
                                    </div>
                                ) : (
                                    <div className="py-1">
                                        {fieldNodes.map(node => renderFieldItem(node))}
                                    </div>
                                )}
                            </ScrollArea>
                        </PopoverContent>
                    </Popover>
                )}
            </div>

            {/* Raw value editor (for advanced editing) */}
            <Input
                value={value}
                onChange={handleInputChange}
                placeholder={placeholder}
                disabled={disabled}
                className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
                Use {'{{field}}'} syntax. For nested fields: {'{{field.subfield}}'}
            </p>
        </div>
    );
}
